import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import { DropdownAddPet } from '../DropdownAddPet';
import Shimmer from '../Shimmer';
import {
  PRIVACY_POLICY_LINK,
  PetGender,
  PetType,
  TERMS_OF_USE_LINK_GENERAL,
} from '../../../constants';
import { AddPetSpecieSelector } from './AddPetSpecieSelector';
import {
  saveNewPetType,
  saveNewPetSex,
  changeTermsAndPolicyCheckbox,
} from '../../../actions/quoting';
import { AddPetGenderSelector } from './AddPetGenderSelector';
import Checkbox from '../Checkbox';
import { FigoInput } from '../FigoInput';
import { useResponsive } from '../../../hooks';
import { URL_ROOT_QUOTE } from '../../../routes';

const AddPetForm = ({
  ageError = '',
  breedError = '',
  emailError = '',
  getQuote = false,
  handleEmail = () => { },
  handleOnCancelClick = () => { },
  handlePetNameChange = () => { },
  handleSubmitPetForm = () => { },
  handleZipcodeChange = () => { },
  isEditing = false,
  onBlurEmail = () => { },
  onBlurPetName = () => { },
  onBlurZipcode = () => { },
  onFocusEmail = () => { },
  onFocusPetName = () => { },
  onFocusZipcode = () => { },
  onKeyPress = () => { },
  petAgeRef = null,
  petBreedRef = null,
  petEmailRef = null,
  petGenderError = '',
  petGenderRef = null,
  petNameError = '',
  petNameRef = null,
  petTypeError = '',
  petTypeRef = null,
  petZipcodeRef = null,
  setPetGenderError = () => { },
  setPetTypeError = () => { },
  setTermsPolicyError = () => { },
  show,
  termsPolicyError = false,
  zipCodeError = '',
}) => {
  const store = useSelector((stores) => stores.quoting);
  const {
    acceptedTermsAndPolicy,
    ageData,
    sessionInformation,
    isBreedDataLoading,
    newPet: {
      breedCat,
      breedDog,
      email,
      petAge,
      petBreed,
      petName,
      petSex,
      petType,
      zipcode,
    },
    parameters,
  } = store;

  const { t } = useTranslation('quoting');
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const showShimmer = !ageData && !breedDog.length && !breedCat.length;
  const shimmerWidth = (getQuote || showShimmer) ? { width: '100%' } : {};

  const breedList = useMemo(() => {
    if (petType.value === PetType.None.value) {
      return [];
    }

    const COMES_FIRST = -1;
    const COMES_LAST = 1;
    const SAME_ORDER = 0;

    const breeds = petType.value === PetType.Dog.value ? breedDog : breedCat;
    const breedsCopy = [...breeds];

    return breedsCopy.sort((prev, next) => {
      const prevMixed = prev.BreedName?.toUpperCase().includes('MIXED');
      const nextMixed = next.BreedName?.toUpperCase().includes('MIXED');

      if (prevMixed === nextMixed) {
        return SAME_ORDER;
      }

      return prevMixed ? COMES_FIRST : COMES_LAST;
    });
  }, [petType, breedDog, breedCat]);

  const dogSelected = petType.value === PetType.Dog.value
    ? {
      label: 'Dog Selected',
      selected: true,
    } : {
      label: 'Dog UnSelected',
      selected: false,
    };

  const catSelected = petType.value === PetType.Cat.value
    ? {
      label: 'Cat Selected',
      selected: true,
    } : {
      label: 'Cat UnSelected',
      selected: false,
    };

  const petGenderSelected = () => {
    if (petSex.value === PetGender.None.value) {
      return {
        femaleGender: {
          className: '',
          label: `${PetGender.Female.name} Gender UnSelected`,
          selected: false,
        },
        maleGender: {
          className: '',
          label: `${PetGender.Male.name} Gender UnSelected`,
          selected: false,
        },
      };
    }

    return (
      petSex.value === PetGender.Male.value
        ? {
          femaleGender: {
            className: '',
            label: `${PetGender.Female.name} Gender UnSelected`,
            selected: false,
          },
          maleGender: {
            className: ' Modal-add-pet-gender-selector-item-selected',
            label: `${PetGender.Male.name} Gender Selected`,
            selected: true,
          },
        } : {
          femaleGender: {
            className: ' Modal-add-pet-gender-selector-item-selected',
            label: `${PetGender.Female.name} Gender Selected`,
            selected: true,
          },
          maleGender: {
            className: '',
            label: `${PetGender.Male.name} Gender UnSelected`,
            selected: false,
          },
        });
  };

  const onCancelClick = (event) => {
    event.preventDefault();

    handleOnCancelClick();
  };

  const onChangeAcceptTerms = useCallback(() => {
    dispatch(changeTermsAndPolicyCheckbox(!acceptedTermsAndPolicy));
    setTermsPolicyError(acceptedTermsAndPolicy);
  }, [dispatch, acceptedTermsAndPolicy, setTermsPolicyError]);

  useEffect(() => {
    if (store.message === '' && !getQuote && show) {
      petNameRef.current.focus();
    }
  }, [
    petNameRef,
    store.message,
    getQuote,
    show,
  ]);

  const getZipCodeValue = useCallback(() => {
    if (getQuote || isEditing) {
      return zipcode;
    }

    return parameters && parameters.petZipCode;
  }, [getQuote, isEditing, parameters, zipcode]);

  const progressIdCount = useMemo(() => {
    const valuesArray = [{
      input: 'petName',
      value: petName,
    },
    {
      input: 'petType',
      value: petType.name,
    },
    {
      input: 'petGender',
      value: petSex.name,
    },
    {
      input: 'zipCode',
      value: getZipCodeValue(),
    },
    {
      input: 'user-email',
      value: email,
    },
    {
      input: 'AgeSearchInput',
      value: petAge || '',
    },
    {
      input: 'BreedSearchInput',
      value: petBreed || '',
    }];

    return valuesArray.filter((item) => item.value === '').length;
  }, [
    email,
    getZipCodeValue,
    petAge,
    petBreed,
    petName,
    petSex.name,
    petType.name,
  ]);

  const progressQueue = useMemo(
    () => t('progressQueueText', {
      count: progressIdCount,
      numberOfFields: progressIdCount,
    }),
    [progressIdCount, t],
  );

  function renderButtons() {
    if (getQuote) {
      return (
        <>
          <Checkbox
            checked={acceptedTermsAndPolicy}
            className="Modal-add-pet-terms-and-privacy"
            containerClassName="Modal-add-pet-terms-and-policy-wrapper"
            error={termsPolicyError}
            id="terms-and-privacy"
            onClick={onChangeAcceptTerms}
          >
            <Trans
              components={{
                policy: (
                  <a
                    aria-label={t('privacyLink')}
                    className="Modal-add-pet-links Modal-add-pet-link-margin"
                    href={PRIVACY_POLICY_LINK}
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
                term: (
                  <a
                    aria-label={t('termsLink')}
                    className="Modal-add-pet-links"
                    href={TERMS_OF_USE_LINK_GENERAL}
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
              }}
              i18nKey="termsAndPrivacy"
              ns="quoting"
            />
          </Checkbox>

          <button
            aria-describedby="progressQueueId"
            className="Modal-add-pet-get-quote-button"
            data-kameleoon-destination={URL_ROOT_QUOTE}
            onClick={handleSubmitPetForm}
            type="button"
          >
            {t('getQuote')}
          </button>

          <p
            className="Modal-add-pet-log"
            id="progressQueueId"
          >
            {progressQueue}
          </p>
        </>
      );
    }

    return (
      <>
        <button
          className="Modal-add-pet-footer-cancel-button"
          onClick={onCancelClick}
          type="button"
        >
          {t('cancel')}
        </button>

        <button
          aria-describedby="progressQueueId"
          className="Modal-add-pet-footer-save-button"
          onClick={handleSubmitPetForm}
          type="button"
        >
          {isEditing ? 'Continue' : 'Save'}
        </button>

        <p
          className="Modal-add-pet-log"
          id="progressQueueId"
        >
          {progressQueue}
        </p>
      </>
    );
  }

  function zipCodeEditable() {
    if (sessionInformation.nopCommerceUser.LoginSuccess) {
      return false;
    }

    return getQuote || (isEditing
      && store && store.data.ebPetQuoteResponseList.length === 1);
  }

  const handleDogSelected = useCallback((event) => {
    event.preventDefault();

    if (petType !== PetType.Dog) {
      dispatch(saveNewPetType(PetType.Dog));
    }

    setPetTypeError('');
  }, [dispatch, petType, setPetTypeError]);

  const handleCatSelected = useCallback((event) => {
    event.preventDefault();

    if (petType !== PetType.Cat) {
      dispatch(saveNewPetType(PetType.Cat));
    }

    setPetTypeError('');
  }, [dispatch, petType, setPetTypeError]);

  const handleFemaleGenderSelected = useCallback((event) => {
    event.preventDefault();

    dispatch(saveNewPetSex(PetGender.Female));
    setPetGenderError('');
  }, [dispatch, setPetGenderError]);

  const handleMaleGenderSelected = useCallback((event) => {
    event.preventDefault();

    dispatch(saveNewPetSex(PetGender.Male));
    setPetGenderError('');
  }, [dispatch, setPetGenderError]);

  return (
    <form
      aria-busy={showShimmer}
      className="Modal-add-pet-form"
    >
      <Shimmer
        className={`Modal-add-pet-input-wrapper${getQuote
          ? ' Modal-add-pet-input-wrapper-landing' : ''}`}
        visible={showShimmer}
        {...shimmerWidth}
      >
        <FigoInput
          ref={petNameRef}
          aria-invalid={petNameError}
          aria-required
          error={!!petNameError}
          id="petName"
          label={t('petName')}
          message={petNameError}
          name="petName"
          noBlankSpace
          onBlur={onBlurPetName}
          onChange={handlePetNameChange}
          onFocus={onFocusPetName}
          placeholder={t('requiredPlaceholder')}
          type="text"
          value={petName}
        />
      </Shimmer>

      <Shimmer
        className={`Modal-add-pet-input-wrapper ${getQuote
          ? ' Modal-add-pet-input-wrapper-landing' : ''}`}
        visible={showShimmer}
        {...shimmerWidth}
      >
        <FigoInput
          ref={petZipcodeRef}
          aria-invalid={zipCodeError}
          aria-required
          disabled={!zipCodeEditable()}
          error={!!zipCodeError}
          id="zipCode"
          label={t('zipCode')}
          maxLength={5}
          message={zipCodeError}
          name="zipCode"
          noBlankSpace
          onBlur={onBlurZipcode}
          onChange={handleZipcodeChange}
          onFocus={onFocusZipcode}
          placeholder={t('requiredPlaceholder')}
          type="text"
          value={getZipCodeValue()}
        />
      </Shimmer>

      <div
        className={`Modal-add-pet-input-wrapper
        ${getQuote ? 'Modal-add-pet-input-wrapper-landing' : ''}`}
      >
        <AddPetSpecieSelector
          catSelected={catSelected}
          dogSelected={dogSelected}
          dogSpeciesRef={petTypeRef}
          error={petTypeError}
          handleCatSelected={handleCatSelected}
          handleDogSelected={handleDogSelected}
          noBlankSpace
        />
      </div>

      <div
        className={`Modal-add-pet-input-wrapper
        ${getQuote ? 'Modal-add-pet-input-wrapper-landing' : ''}`}
      >
        <AddPetGenderSelector
          error={petGenderError}
          handleFemaleGenderSelected={handleFemaleGenderSelected}
          handleMaleGenderSelected={handleMaleGenderSelected}
          maleGenderRef={petGenderRef}
          noBlankSpace
          petGenderSelected={petGenderSelected()}
        />
      </div>

      <div
        className={`Modal-add-pet-input-wrapper
        ${getQuote ? 'Modal-add-pet-input-wrapper-landing' : ''}`}
      >
        <DropdownAddPet
          isLanding={getQuote}
          items={ageData}
          noBlankSpace
          petAgeRef={petAgeRef}
          placeholder={t('requiredPlaceholder')}
          requiredError={ageError}
          showShimmer={showShimmer}
          title={t('petForm.age')}
          type="age"
          withInput
        />
      </div>

      <div
        className={`Modal-add-pet-input-wrapper
        ${getQuote ? 'Modal-add-pet-input-wrapper-landing' : ''}`}
      >
        <DropdownAddPet
          isLanding={getQuote}
          items={breedList}
          noBlankSpace
          petBreedRef={petBreedRef}
          placeholder="Start typing to search..."
          requiredError={breedError}
          shouldOpenUp={isMobile}
          showLoading={isBreedDataLoading}
          showShimmer={showShimmer}
          title={t('petForm.breed')}
          type="breed"
          withInput
        />
      </div>

      {getQuote
        && (
          <Shimmer
            className={`Modal-add-pet-input-wrapper${getQuote
              ? ' Modal-add-pet-input-wrapper-landing' : ''}`}
            visible={showShimmer}
            {...shimmerWidth}
          >
            <FigoInput
              ref={petEmailRef}
              aria-invalid={emailError}
              aria-required
              error={!!emailError}
              id="user-email"
              label={t('email')}
              message={emailError}
              name="email"
              noBlankSpace
              onBlur={onBlurEmail}
              onChange={handleEmail}
              onFocus={onFocusEmail}
              onKeyDown={onKeyPress}
              placeholder={t('requiredPlaceholder')}
              type="email"
              value={email}
            />
          </Shimmer>
        )}

      <Shimmer
        className={'Modal-add-pet-footer-buttons'
          + `${getQuote ? ' Modal-add-pet-footer-buttons-col' : ''}`}
        visible={showShimmer}
      >
        {renderButtons()}
      </Shimmer>
    </form>
  );
};

export { AddPetForm };
